import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import {
  Breadcrumb,
  BackdropCircular,
  ConfirmDialog
} from 'components';
import {
  Typography,
  Grid,
  Switch,
  TextField,
  Card,
  Button,
  Icon,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  getStoreEarningRulesApi,
  getStoreSpendingRulesApi,
  getStoreConfigsApi,
  updateStoreConfigsApi,
  updateStoreSiteMapApi,
  getStoreRewardBehaviourRulesApi,
  saveStoreRewardBehaviourRulesApi,
  getStoreProductsApi,
  getStoreBrandsApi,
  getStoreCategoriesApi,
  getStoreCustomerGroupsApi,
} from 'api';
import {
  changeBehaviourRuleConfigs
} from 'redux/actions/marketing_rules';
import RedirectRewardContentDialog from 'components/Dialogs/RedirectRewardContentDialog';
import RewardInfoEditor from '../RewardInfoEditor';
import RewardEarnRule from './RewardEarnRule';
import RewardSpendRule from './RewardSpendRule';


const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];

const RewardConfigs = () => {
  const dispatch = useDispatch();
  const [openRewardInfoEditor, setOpenRewardInfoEditor] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rewardConfigValue, setRewardConfigValue] = useState(false);

  const {
    earningRules,
    loadingEarningRules,
    loadingSpendingRules,
    spendingRules,
    loadingSavingEarningRules,
    loadingSavingSpendingRules,
    loadingRewardBehaviourRules,
    rewardBehaviourRules,
    rewardBehaviourRulesToUpdate,
    loadingSaveRewardBehaviourRules
  } = useSelector((state) => state.marketing_rules);
  const { storeBrands } = useSelector((state) => state.brands);
  const {store_products: {catalogProducts}, productsLoading} = useSelector((state) => state.products);
  const {storeActiveCategoriesWithActiveNavLevel, loadingCategories} = useSelector((state) => state.categories);
  const {storeConfigs, rewardConfigsToUpdate, loading, error, loadingUpdateStoreConfigs} = useSelector((state) => state.store_configs);
  const {customerGroups = []} = useSelector((state) => state.customers);
  const allCustomerGroups = [...defaultCustomerGroups, ...customerGroups];
  const {loadingUpdateCmsPages} = useSelector((state) => state.cms_pages);
  const [openDialogRewardPage, setOpenDialogRewardPage] = useState(false);

  const loader = loadingEarningRules ||
  loadingSpendingRules ||
  loadingRewardBehaviourRules ||
  loadingSaveRewardBehaviourRules ||
  productsLoading || loadingCategories;

  const saveLoader =  loadingSavingEarningRules ||
  loadingSavingSpendingRules ||
  loadingUpdateCmsPages ||
  loadingSaveRewardBehaviourRules ||
  loadingUpdateStoreConfigs;

  useEffect(() => {
    if (!earningRules.length) {
      getStoreEarningRulesApi({dispatch});
    }

    if (!spendingRules.length) {
      getStoreSpendingRulesApi({dispatch});
    }

    if (!storeConfigs.length) {
      getStoreConfigsApi({dispatch});
    }

    if (!storeBrands.length) {
      getStoreBrandsApi({dispatch});
    }

    if (!catalogProducts.length) {
      getStoreProductsApi({dispatch});
    }

    if (!storeActiveCategoriesWithActiveNavLevel.length) {
      getStoreCategoriesApi({dispatch, checkStoreType: false});
    }

    getStoreCustomerGroupsApi({dispatch});

    getStoreRewardBehaviourRulesApi({dispatch});
  }, []);

  const handleChangeBehaviourRuleConfigs = (event, ruleName) => {
    const {name, checked, type, value} = event.target;
    dispatch(changeBehaviourRuleConfigs(name, checked, type, value, ruleName));
  };

  const handleSave = async (rule_id) => {
    if (rewardBehaviourRulesToUpdate.length) {
      const rule = rewardBehaviourRulesToUpdate.find((savedRule) => savedRule.rule_id === rule_id);

      const isSuccess = await saveStoreRewardBehaviourRulesApi({dispatch, rule});

      if (isSuccess) {
        setOpenDialogRewardPage(true);
      }
    }

    if (rewardConfigsToUpdate) {
      updateStoreConfigsApi({dispatch, configs: [rewardConfigsToUpdate]});
    }
  };

  const handleUpdateRewardConfigs = () => {
    const configs = [
      {
        path: 'rewardpoints/general/enabled',
        value: rewardConfigValue ? '1' : '0'
      },
      {
        path: 'rewardpoints/nextech_integration/enable_emails',
        value: rewardConfigValue ? '1' : '0'
      },
    ];
    updateStoreConfigsApi({dispatch, configs});
    updateStoreSiteMapApi({dispatch, isEnabled: rewardConfigValue});
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <div>
        <Breadcrumb
          routeSegments={[
            { name: 'Settings', path: '/settings#go_live_requirements' },
            { name: 'Store Configurations', path: '/settings#store_configurations' },
            { name: 'Rewards' }
          ]}
        />
      </div>
      <div className="m-10">
        <Card elevation={25} className="p-8">
          {openRewardInfoEditor ?
            <RewardInfoEditor setOpenRewardInfoEditor={setOpenRewardInfoEditor} />
            :
            <>
              <Button
                color="primary"
                variant="contained"
                className="mb-6"
                onClick={() => setOpenRewardInfoEditor(true)}
              >
                <Icon className="mr-2">edit</Icon>
                Edit Rewards Program Content
              </Button>
              <Typography variant="h4" className="my-4">Reward Points Rules</Typography>
              {!(loading && error) && (storeConfigs?.length > 0) &&
              <Grid container spacing={3} className="ml-2 flex items-center">
                <Grid item xs={12} sm={4} md={3}>Enable Reward Points:</Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <Switch
                    color="primary"
                    name="rewardpoints/general/enabled"
                    checked={storeConfigs.find((config) => config.path === 'rewardpoints/general/enabled').value == 1}
                    onChange={(event) => {
                      setConfirmOpen(true);
                      setRewardConfigValue(event.target.checked);
                    }}
                  />
                </Grid>
              </Grid>
              }
              <Accordion elevation={0}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                  <Typography variant="h5" className="my-4">Shopping Cart Earning Rules</Typography>
                </AccordionSummary>
                <AccordionDetails className="flex-column">
                  {!loadingEarningRules && earningRules?.map((rule) => {
                    return (
                      <div key={rule.rule_id}>
                        <RewardEarnRule
                          rule={rule}
                          products={catalogProducts}
                          brands={storeBrands}
                          categories={storeActiveCategoriesWithActiveNavLevel}
                          setOpenDialogRewardPage={setOpenDialogRewardPage}
                        />
                      </div>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
              <Accordion elevation={0}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                  <Typography variant="h5" className="my-4">Shopping Cart Spending Rules</Typography>
                </AccordionSummary>
                <AccordionDetails  className="flex-column">
                  {!loadingSpendingRules && spendingRules?.map((rule) => {
                    return (
                      <div key={rule.id}>
                        <RewardSpendRule
                          rule={rule}
                          products={catalogProducts}
                          brands={storeBrands}
                          categories={storeActiveCategoriesWithActiveNavLevel}
                          setOpenDialogRewardPage={setOpenDialogRewardPage}
                        />
                      </div>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
              {Object.keys(rewardBehaviourRules).map((ruleName) => {
                const {
                  rule_id,
                  is_active,
                  point_action,
                  point_amount,
                  max_point,
                  customer_group_ids,
                  // max_point_period
                } = rewardBehaviourRules[ruleName];
                return (
                  <Accordion elevation={0} key={rule_id}>
                    <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                      <Typography variant="h5" className="my-4 capitalize">{ruleName} Earning Rules</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={4} md={3}>Enable:</Grid>
                        <Grid item xs={12} sm={8} md={9}>
                          <Switch
                            color="primary"
                            name="is_active"
                            checked={is_active == 1}
                            onChange={(event) => handleChangeBehaviourRuleConfigs(event, ruleName)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>Point Amount:</Grid>
                        <Grid item xs={12} sm={8} md={9}>
                          <NumberFormat
                            customInput={TextField}
                            allowNegative={false}
                            label="Point Amount"
                            name="point_amount"
                            variant="outlined"
                            value={point_amount}
                            decimalScale={0}
                            onChange={(event) => handleChangeBehaviourRuleConfigs(event, ruleName)}
                            helperText="Amount of points to earn on one action"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                          Customer Groups <span className="text-error">*</span>
                        </Grid>
                        <Grid item xs={12} sm={8} md={9}>
                          <Autocomplete
                            multiple
                            fullWidth
                            disableClearable
                            size="small"
                            className="max-w-600"
                            id="customer_group_ids"
                            options={allCustomerGroups}
                            value={allCustomerGroups.filter((g) => customer_group_ids?.includes(String(g.id)))}
                            getOptionLabel={(option) => option.code}
                            onChange={(event, value, action, option) => {
                              const selectedOptionId = String(option?.option?.id);
                              const {checked, type} = event.target;
                              let customerGroups = [];

                              if (action === 'select-option') {
                                customerGroups = value !== null ? [...customer_group_ids, selectedOptionId] : customer_group_ids;
                              }

                              if (action === 'remove-option') {
                                customerGroups = value !== null ? customer_group_ids.filter((id) => id !== selectedOptionId) : customer_group_ids;
                              }

                              dispatch(changeBehaviourRuleConfigs('customer_group_ids', checked, type, customerGroups, ruleName));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                error={!customer_group_ids.length}
                                helperText={!customer_group_ids.length && 'Field is required'}
                                variant="outlined"
                                label="Customer Groups"
                                placeholder="Choose customer group"
                              />
                            )}
                          />
                        </Grid>
                        {point_action === 3 &&
                        <>
                          <Grid item xs={12} sm={4} md={3}>Maximum Earning Points Per Day:</Grid>
                          <Grid item xs={12} sm={8} md={9}>
                            <NumberFormat
                              customInput={TextField}
                              allowNegative={false}
                              label="Maximum Earning Points Per Day"
                              name="max_point"
                              decimalScale={0}
                              variant="outlined"
                              value={max_point}
                              onChange={(event) => handleChangeBehaviourRuleConfigs(event, ruleName)}
                              helperText="Set the maximum number of earning points. If zero, there is no limitation"
                            />
                          </Grid>
                        </>
                        }
                        <Grid item xs={12}>
                          <div className="flex justify-end">
                            <Button
                              variant="contained"
                              color="primary"
                              className="mt-10 min-w-200"
                              onClick={() => handleSave(rule_id)}
                            >
                              Save Rule
                            </Button>
                          </div>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          }
        </Card>
        <BackdropCircular loading={loader || saveLoader} title={saveLoader ? 'Saving...' : 'Preparing values...'} />
        <ConfirmDialog
          title="Change Rewards Status?"
          open={confirmOpen}
          setOpen={setConfirmOpen}
          onConfirm={handleUpdateRewardConfigs}
        >
          Are you sure you want to {rewardConfigValue ? 'enable' : 'disable'} rewards on your store?
        </ConfirmDialog>
        <RedirectRewardContentDialog
          open={openDialogRewardPage}
          setOpen={setOpenDialogRewardPage}
          setOpenRewardInfoEditor={setOpenRewardInfoEditor}
        />
      </div>
    </div>
  );
};

export default RewardConfigs;
